import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { defineMessages, useIntl } from 'react-intl'
import { View, ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { FileValidationErrorCode, FileValidationResult } from '@lyrahealth-inc/shared-app-logic'

import { BaseInput, BaseInputProps } from './BaseInput'
import { IS_WEB } from '../../constants'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils'
import { type FileWithUri, pickImageFromLibrary } from '../../utils/expoImagePicker'
import { BodyText } from '../bodyText/BodyText'
import { LoadingSpinner } from '../icons/LoadingSpinner'
import { UploadIcon } from '../icons/UploadIcon'
import { XIcon } from '../icons/XIcon'
import { Image } from '../image/Image'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'

// Shared container height for upload and preview states
const DEFAULT_CONTAINER_HEIGHT = 100

const MAX_PREVIEW_FILE_SIZE = 5 * 1024 * 1024 // 5MB in bytes

const FILE_UPLOAD_MESSAGES = defineMessages({
  fileTooLarge: {
    defaultMessage: 'File is too large',
    description: 'File too large error message',
  },
  unsupportedFileType: {
    defaultMessage: 'Invalid file type',
    description: 'Invalid file type error message',
  },
  uploadFailed: {
    defaultMessage: 'Upload failed',
    description: 'Upload failed error message',
  },
  imageSelectionFailed: {
    defaultMessage: 'Failed to select image',
    description: 'Error message when image selection fails',
  },
})

const FileUploadContainer = styled(View)<{ theme: ThemeType; hovered: boolean; uploading: boolean; height: number }>(
  ({ theme, hovered, uploading, height }) => ({
    borderWidth: 1,
    borderStyle: uploading ? 'solid' : 'dashed',
    borderColor: hovered || uploading ? theme.colors.borderActive : theme.colors.inputOutlineDefault,
    borderRadius: 8,
    padding: '24px 16px',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height,
    backgroundColor: hovered || uploading ? theme.colors.backgroundHighlightPrimary : theme.colors.backgroundPrimary,
  }),
)

const UploadContent = styled(View)({
  justifyContent: 'center',
  flexDirection: 'row',
  gap: 12,
  alignItems: 'center',
})

const FilePreviewContainer = styled(View)<{ theme: ThemeType; height: number }>(({ theme, height }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.colors.borderDefault,
  borderRadius: 8,
  padding: 8,
  alignItems: 'center',
  justifyContent: 'center',
  height,
  width: '100%',
  backgroundColor: theme.colors.backgroundPrimary,
  position: 'relative',
}))

const ClearButton = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  width: 28,
  height: 28,
  borderRadius: 14,
  border: `2px solid ${theme.colors.iconDefault}`,
  backgroundColor: theme.colors.backgroundSecondary,
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
}))

const PreviewImageContainer = styled(View)({
  width: '100%',
  height: '100%',
  borderRadius: 4,
  overflow: 'hidden',
  alignItems: 'center',
  justifyContent: 'center',
})

export interface FileUploadProps extends Omit<BaseInputProps, 'children'> {
  placeholder?: string
  disabled?: boolean
  baseInputStyle?: ViewStyle
  name?: string
  isUploading?: boolean
  onFileSelect?: (file: File | null) => void
  acceptedFileTypes?: string[]
  maxFileSize?: number
  selectedFile?: File | null
  onUpload?: (file: File) => Promise<void>
  onUploadComplete?: (uploadedFile: File) => void
  showPreview?: boolean
  allowClear?: boolean
  errorOnSubmitFailed?: boolean
  containerHeight?: number
  onError?: () => void
  validateFile?: (file: File) => FileValidationResult
}

export const FileUpload: FunctionComponent<FileUploadProps> = ({
  label,
  error,
  placeholder,
  disabled = false,
  baseInputStyle,
  name,
  isUploading = false,
  onFileSelect,
  acceptedFileTypes,
  maxFileSize,
  selectedFile: controlledSelectedFile,
  onUpload,
  onUploadComplete,
  showPreview = true,
  allowClear = true,
  containerHeight = DEFAULT_CONTAINER_HEIGHT,
  onError,
  validateFile,
  ...rest
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [hovered, setHovered] = useState(false)
  const [internalSelectedFile, setInternalSelectedFile] = useState<File | null>(null)
  const [uploadingState, setUploadingState] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [errorText, setErrorText] = useState<string | null>(null)

  const imageTypes = useMemo(() => acceptedFileTypes ?? ['image/jpeg', 'image/png', 'image/webp'], [acceptedFileTypes])
  const hasFile = !!(controlledSelectedFile ?? internalSelectedFile)
  const currentlyUploading = isUploading || uploadingState
  const fileForPreview = (controlledSelectedFile ?? internalSelectedFile) || null
  const showFilePreview = hasFile && showPreview && !currentlyUploading

  const isImageFile = (file: File): boolean => {
    return imageTypes.includes(file.type)
  }

  const defaultValidateFile = (file: File): FileValidationResult => {
    if (maxFileSize && file.size > maxFileSize) {
      return {
        isValid: false,
        error: {
          code: FileValidationErrorCode.FILE_TOO_LARGE,
        },
      }
    }

    return {
      isValid: true,
    }
  }

  // Image preview generation
  useEffect(() => {
    const file = fileForPreview as FileWithUri | null

    if (!file) {
      setPreviewUrl(null)
      return
    }

    // Skip preview generation for large files
    if (file.size > MAX_PREVIEW_FILE_SIZE) {
      setPreviewUrl(null)
      return
    }

    // On React Native, use the native URI directly for image preview
    if (!IS_WEB && file.uri) {
      setPreviewUrl(file.uri)
      return () => {
        setPreviewUrl(null)
      }
    }

    // On web, use URL.createObjectURL for blob preview
    if (IS_WEB && typeof URL !== 'undefined' && URL.createObjectURL) {
      try {
        const url = URL.createObjectURL(file)
        setPreviewUrl(url)
        return () => {
          if (url && typeof URL !== 'undefined' && URL.revokeObjectURL) {
            URL.revokeObjectURL(url)
          }
        }
      } catch {
        setPreviewUrl(null)
      }
    } else {
      setPreviewUrl(null)
    }

    return
  }, [fileForPreview])

  /**
   * Process and validate a selected file
   * Shared logic for both web file input and native mobile image picker
   */
  const handleFileSelection = async (file: File) => {
    setErrorText(null)

    // Use custom validation if provided, otherwise fallback to default validation
    const validationResult: FileValidationResult = validateFile ? validateFile(file) : defaultValidateFile(file)

    if (!validationResult.isValid) {
      let errorMessage = ''
      const errorCode = validationResult.error?.code

      if (errorCode === FileValidationErrorCode.FILE_TOO_LARGE) {
        errorMessage = formatMessage(FILE_UPLOAD_MESSAGES.fileTooLarge)
      } else if (errorCode === FileValidationErrorCode.UNSUPPORTED_FILE_TYPE) {
        errorMessage = formatMessage(FILE_UPLOAD_MESSAGES.unsupportedFileType)
      }

      setErrorText(errorMessage)
      console.error('File validation failed', {
        errorCode,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      })

      // Reset file state
      onFileSelect?.(null)
      setInternalSelectedFile(null)
      onError?.()
      return
    }

    if (onUpload) {
      try {
        setUploadingState(true)
        await onUpload(file)
        onFileSelect?.(file)
        setInternalSelectedFile(file)
        onUploadComplete?.(file)
      } catch {
        setErrorText(formatMessage(FILE_UPLOAD_MESSAGES.uploadFailed))
        onFileSelect?.(null)
        setInternalSelectedFile(null)
        onError?.()
      } finally {
        setUploadingState(false)
      }
    } else {
      onFileSelect?.(file)
      setInternalSelectedFile(file)
      onUploadComplete?.(file)
    }
  }

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null

    if (!file) {
      onFileSelect?.(null)
      setInternalSelectedFile(null)
      return
    }

    await handleFileSelection(file)

    // Reset the input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handlePress = async () => {
    if (disabled || currentlyUploading) return
    if (IS_WEB && typeof document !== 'undefined' && fileInputRef.current) {
      fileInputRef.current.click()
    } else {
      // Native mobile: Use expo-image-picker to select image from photo library
      // TODO: This only supports image selection, need to add file support
      try {
        const file = await pickImageFromLibrary()

        if (file) {
          await handleFileSelection(file)
        }
      } catch (error) {
        console.error('[FileUpload] Failed to pick image from library', { error })
        setErrorText(formatMessage(FILE_UPLOAD_MESSAGES.imageSelectionFailed))
        onError?.()
      }
    }
  }

  const handleClearFile = () => {
    onFileSelect?.(null)
    setInternalSelectedFile(null)
    setErrorText(null)
    if (previewUrl && IS_WEB && typeof URL !== 'undefined' && URL.revokeObjectURL) {
      URL.revokeObjectURL(previewUrl)
    }
  }

  const displayText =
    (currentlyUploading
      ? formatMessage({ defaultMessage: 'Uploading...', description: 'Uploading state' })
      : placeholder) ||
    formatMessage({
      defaultMessage: 'Upload',
      description: 'Placeholder label for file upload',
    })

  const accessibilityLabel = hasFile
    ? formatMessage({ defaultMessage: 'Selected file. Tap to change file.', description: 'Selected file label' })
    : formatMessage({ defaultMessage: 'Upload file', description: 'Accessibility label for file upload' })

  const renderContent = () => {
    if (currentlyUploading) {
      return (
        <FileUploadContainer
          testID={name ? `${name}-uploading` : 'uploading'}
          hovered={false}
          uploading
          height={containerHeight}
        >
          <LoadingSpinner size={24} />
          <BodyText
            text={formatMessage({
              defaultMessage: 'Uploading...',
              description: 'Text displayed while uploading in progress',
            })}
            size={BodyTextSize.DEFAULT}
            color={colors.textActive}
            textAlign='center'
          />
        </FileUploadContainer>
      )
    }

    if (showFilePreview && fileForPreview) {
      const isImage = isImageFile(fileForPreview)
      return (
        <FilePreviewContainer height={containerHeight}>
          {allowClear && (
            <ClearButton
              onPress={handleClearFile}
              testID={name ? `${name}-clear-file` : 'clear-file'}
              accessibilityRole='button'
              accessibilityLabel={formatMessage({
                defaultMessage: 'Remove file',
                description: 'Accessibility label to remove uploaded file',
              })}
            >
              <XIcon size={18} />
            </ClearButton>
          )}
          {isImage && previewUrl ? (
            <PreviewImageContainer testID={name ? `${name}-image-preview` : 'image-preview'}>
              <Image
                source={{ uri: previewUrl }}
                alt={formatMessage({
                  defaultMessage: 'Preview image',
                  description: 'Accessibility label for an image preview',
                })}
                contentFit='contain'
                style={{ width: '100%', height: '100%' }}
                accessibilityIgnoresInvertColors
              />
            </PreviewImageContainer>
          ) : (
            <UploadContent>
              <UploadIcon size={21} fillColor={colors.iconDefault} />
              <BodyText
                text={formatMessage({
                  defaultMessage: 'File selected',
                  description: 'Text displayed when a file is selected',
                })}
                size={BodyTextSize.DEFAULT}
                color={colors.textActive}
                textAlign='center'
              />
            </UploadContent>
          )}
        </FilePreviewContainer>
      )
    }

    return (
      <FileUploadContainer hovered={hovered} uploading={false} height={containerHeight}>
        <UploadContent>
          <View>
            <UploadIcon size={21} fillColor={disabled ? colors.textInactive : colors.iconActive} />
          </View>
          <BodyText text={displayText} size={BodyTextSize.DEFAULT} color={colors.linkDefault} textAlign='center' />
        </UploadContent>
      </FileUploadContainer>
    )
  }

  return (
    <BaseInput name={name} label={label} error={error || errorText || undefined} style={baseInputStyle} {...rest}>
      {/* Hidden file input for web */}
      {IS_WEB && typeof document !== 'undefined' && (
        <input
          ref={fileInputRef}
          type='file'
          accept={imageTypes.join(',') || '*/*'}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          data-testid={name ? `${name}-file-input` : 'file-input'}
          aria-hidden='true'
        />
      )}
      {showFilePreview ? (
        <View testID={name ? `${name}-upload-area` : 'file-upload-area'}>{renderContent()}</View>
      ) : (
        <PressableOpacity
          onPress={handlePress}
          disabled={disabled || currentlyUploading}
          testID={name ? `${name}-upload-area` : 'file-upload-area'}
          accessibilityRole='button'
          accessibilityLabel={accessibilityLabel}
          onHoverIn={() => setHovered(true)}
          onHoverOut={() => setHovered(false)}
        >
          {renderContent()}
        </PressableOpacity>
      )}
    </BaseInput>
  )
}

export const FileUploadRFF: FunctionComponent<FieldRenderProps<File | null> & FileUploadProps> = ({
  input: { value, onChange, name },
  meta: { touched, error, submitFailed },
  label,
  placeholder,
  disabled,
  baseInputStyle,
  errorOnSubmitFailed = false,
  isUploading = false,
  onUpload,
  onUploadComplete,
  acceptedFileTypes,
  maxFileSize,
  showPreview = true,
  allowClear = true,
  containerHeight,
  onError,
  validateFile,
  ...rest
}) => {
  const displayError = errorOnSubmitFailed ? submitFailed && error : touched && error

  const handleFileSelect = (file: File | null) => {
    onChange(file)
  }

  return (
    <FileUpload
      label={label}
      error={displayError}
      placeholder={placeholder}
      disabled={disabled}
      baseInputStyle={baseInputStyle}
      name={name}
      isUploading={isUploading}
      onUpload={onUpload}
      onUploadComplete={onUploadComplete}
      onFileSelect={handleFileSelect}
      selectedFile={value}
      acceptedFileTypes={acceptedFileTypes}
      maxFileSize={maxFileSize}
      showPreview={showPreview}
      allowClear={allowClear}
      containerHeight={containerHeight}
      onError={onError}
      validateFile={validateFile}
      {...rest}
    />
  )
}
